import {refreshToken} from "./AuthService";
import {properties} from "../properties";
import {ftAccessToken, ftRefreshToken} from "../constants/Constants";
import { request } from "./HttpService";

export function getPhotoPresigned(contentType) {
    return request("GET", `/user-settings/presigned/image?contentType=${encodeURIComponent(contentType)}`);
}

export function saveUserPhotoKey(key) {
    return request("POST", "/user-settings/insert/image", { key });
}

export function deleteUserPhoto() {
    return request("DELETE", "/user-settings/image");
}

export function fetchHomeContent(firstCall) {
    return new Promise((resolve, reject) => {
        const accessToken = localStorage.getItem(ftAccessToken)?.replaceAll('"', '');
        let xhttp = new XMLHttpRequest();
        xhttp.onreadystatechange = function () {
            if (this.readyState === 4) {
                if (this.status === 200) {
                    resolve(JSON.parse(this.responseText));
                } else if (this.status === 403 && firstCall) {
                    // If the token is expired, try to refresh it
                    refreshToken().then(newAccessToken => {
                        localStorage.setItem(ftAccessToken, JSON.stringify(newAccessToken));
                        // Retry the original request with the new access token
                        fetchHomeContent(false).then(resolve).catch(reject);
                    }).catch(() => {
                        localStorage.removeItem(ftAccessToken);
                        localStorage.removeItem(ftRefreshToken);
                        reject({ error: new Error("Failed to refresh token."), status: 403 });
                    });
                } else {
                    reject(new Error("Failed to fetch content."));
                }
            }
        };
        xhttp.open("GET", properties.host + "/home/content", true);
        xhttp.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
        xhttp.setRequestHeader("Authorization", "Bearer " + accessToken);
        xhttp.send();
    });
}

export function fetchUserContent(firstCall) {
    return new Promise((resolve, reject) => {
        const accessToken = localStorage.getItem(ftAccessToken)?.replaceAll('"', '');
        let xhttp = new XMLHttpRequest();
        xhttp.onreadystatechange = function () {
            if (this.readyState === 4) {
                if (this.status === 200) {
                    resolve(JSON.parse(this.responseText));
                } else if (this.status === 403 && firstCall) {
                    // If the token is expired, try to refresh it
                    refreshToken().then(newAccessToken => {
                        localStorage.setItem(ftAccessToken, JSON.stringify(newAccessToken));
                        // Retry the original request with the new access token
                        fetchUserContent(false).then(resolve).catch(reject);
                    }).catch(() => {
                        localStorage.removeItem(ftAccessToken);
                        localStorage.removeItem(ftRefreshToken);
                        reject({ error: new Error("Failed to refresh token."), status: 403 });
                    });
                } else {
                    reject(new Error("Failed to fetch content."));
                }
            }
        };
        xhttp.open("GET", properties.host + "/users/userinfo", true);
        xhttp.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
        xhttp.setRequestHeader("Authorization", "Bearer " + accessToken);

        xhttp.send();
    });
}

export function fetchAdminContent(firstCall) {
    return new Promise((resolve, reject) => {
        const accessToken = localStorage.getItem(ftAccessToken)?.replaceAll('"', '');
        let xhttp = new XMLHttpRequest();
        xhttp.onreadystatechange = function () {
            if (this.readyState === 4) {
                if (this.status === 200) {
                    resolve(JSON.parse(this.responseText));
                } else if (this.status === 403 && firstCall) {
                    // If the token is expired, try to refresh it
                    refreshToken().then(newAccessToken => {
                        localStorage.setItem(ftAccessToken, JSON.stringify(newAccessToken));
                        // Retry the original request with the new access token
                        fetchAdminContent(false).then(resolve).catch(reject);
                    }).catch(() => {
                        localStorage.removeItem(ftAccessToken);
                        localStorage.removeItem(ftRefreshToken);
                        reject({ error: new Error("Failed to refresh token."), status: 403 });
                    });
                } else {
                    reject(new Error("Failed to fetch admin content."));
                }
            }
        };
        xhttp.open("GET", properties.host + "/admin/all", true);
        xhttp.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
        xhttp.setRequestHeader("Authorization", "Bearer " + accessToken);

        xhttp.send();
    });
}

export function changeUserRole(email, role, firstCall) {
    return new Promise((resolve, reject) => {
        const request = {
            "email": email,
            "role": role,
        }
        const accessToken = localStorage.getItem(ftAccessToken)?.replaceAll('"', '');
        let xhttp = new XMLHttpRequest();
        xhttp.onreadystatechange = function () {
            if (this.readyState === 4) {
                if (this.status === 200) {
                    resolve(JSON.parse(this.responseText));
                } else if (this.status === 403 && firstCall) {
                    // If the token is expired, try to refresh it
                    refreshToken().then(newAccessToken => {
                        localStorage.setItem(ftAccessToken, JSON.stringify(newAccessToken));
                        // Retry the original request with the new access token
                        changeUserRole(email, role, false).then(resolve).catch(reject);
                    }).catch(() => {
                        localStorage.removeItem(ftAccessToken);
                        localStorage.removeItem(ftRefreshToken);
                        reject({ error: new Error("Failed to refresh token."), status: 403 });
                    });
                } else {
                    reject(new Error("Failed to change role."));
                }
            }
        };
        xhttp.open("PUT", properties.host + "/admin/change-role", true);
        xhttp.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
        xhttp.setRequestHeader("Authorization", "Bearer " + accessToken);

        xhttp.send(JSON.stringify(request));
    });
}

export function fetchReviewerWhitelist() {
    return request("GET", "/admin/reviewers");
}

export function addReviewerToWhitelist(reviewer) {
    return request("POST", "/admin/reviewers", reviewer);
}

export function removeReviewerFromWhitelist(id) {
    return request("DELETE", `/admin/reviewers/${id}`);
}

export function fetchShowcasePapers() {
    return request("GET", "/admin/showcase");
}

export function removeShowcasePaper(manuscriptId) {
    return request("DELETE", `/admin/showcase/${manuscriptId}`);
}

export function fetchShowcasePresignedUrl() {
    return request("GET", "/admin/showcase/presigned");
}

export function insertShowcasePaper(key, name, title, authors) {
    return request("POST", "/admin/showcase/insert", { key, name, title, authors });
}

export function downloadShowcaseCsv(startDate, endDate, dateField, firstCall = true) {
    return new Promise((resolve, reject) => {
        const accessToken = localStorage.getItem(ftAccessToken)?.replaceAll('"', '');
        const params = new URLSearchParams();
        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);
        if (dateField) params.append("dateField", dateField);
        const query = params.toString();
        const url = properties.host + "/admin/showcase/export" + (query ? "?" + query : "");
        const xhttp = new XMLHttpRequest();
        xhttp.open("GET", url, true);
        xhttp.responseType = "blob";
        xhttp.setRequestHeader("Authorization", "Bearer " + accessToken);
        xhttp.onreadystatechange = function () {
            if (this.readyState !== 4) return;
            if (this.status === 200) {
                resolve({
                    blob: xhttp.response,
                    disposition: xhttp.getResponseHeader("Content-Disposition"),
                });
            } else if ((this.status === 401 || this.status === 403) && firstCall) {
                refreshToken()
                    .then(newAccessToken => {
                        localStorage.setItem(ftAccessToken, JSON.stringify(newAccessToken));
                        return downloadShowcaseCsv(startDate, endDate, dateField, false).then(resolve).catch(reject);
                    })
                    .catch(() => {
                        localStorage.removeItem(ftAccessToken);
                        localStorage.removeItem(ftRefreshToken);
                        reject(new Error("auth"));
                    });
            } else {
                reject(new Error("Download failed"));
            }
        };
        xhttp.send();
    });
}